import { useEffect, useState, useCallback } from "react";
import { Image as ImageIcon, Trash2, Plus, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const CITY_OPTIONS = [
  { slug: "bethesda-md", name: "Bethesda, MD" },
  { slug: "rockville-md", name: "Rockville, MD" },
  { slug: "silver-spring-md", name: "Silver Spring, MD" },
  { slug: "annapolis-md", name: "Annapolis, MD" },
  { slug: "baltimore-md", name: "Baltimore, MD" },
  { slug: "washington-dc", name: "Washington, DC" },
  { slug: "arlington-va", name: "Arlington, VA" },
  { slug: "alexandria-va", name: "Alexandria, VA" },
  { slug: "fairfax-va", name: "Fairfax, VA" },
];

const EMPTY_FORM = { city_slug: "bethesda-md", before_url: "", after_url: "", caption: "", order: 0 };

export default function GalleryPanel() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/gallery");
      setItems(data.items || []);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setErr(""); setMsg("");
    try {
      await api.post("/admin/gallery", form);
      setMsg("Gallery item added.");
      setForm({ ...EMPTY_FORM, city_slug: form.city_slug });
      load();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this gallery item?")) return;
    try {
      await api.delete(`/admin/gallery/${id}`);
      load();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="gallery-panel">
      <div className="lg:col-span-1">
        <div className="card-surface p-6">
          <div className="eyebrow mb-2">Add before/after</div>
          <h3 className="font-heading text-xl tracking-tight mb-4 flex items-center gap-2">
            <ImageIcon size={18} className="text-forest" /> Upload by URL
          </h3>
          <form onSubmit={submit} className="space-y-3" data-testid="gallery-form">
            <select className="input-base" value={form.city_slug} onChange={set("city_slug")} data-testid="gallery-city">
              {CITY_OPTIONS.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
            </select>
            <input className="input-base" placeholder="Before image URL" value={form.before_url} onChange={set("before_url")} required data-testid="gallery-before" />
            <input className="input-base" placeholder="After image URL" value={form.after_url} onChange={set("after_url")} required data-testid="gallery-after" />
            <input className="input-base" placeholder="Caption (optional)" value={form.caption} onChange={set("caption")} data-testid="gallery-caption" />
            <input className="input-base" type="number" placeholder="Order (0 first)" value={form.order} onChange={(e) => setForm({ ...form, order: Number(e.target.value) })} data-testid="gallery-order" />
            <button type="submit" disabled={saving} className="btn-primary w-full justify-center" data-testid="gallery-submit">
              <Plus size={14} /> {saving ? "Saving…" : "Add to gallery"}
            </button>
            {msg && <div className="text-sm text-forest flex items-center gap-2" data-testid="gallery-success"><CheckCircle2 size={14} />{msg}</div>}
            {err && <div className="text-sm text-red-700">{err}</div>}
          </form>
          <p className="text-xs text-muted2 mt-4">
            Tip: Use Imgur, Cloudinary, or any public image URL. Recommended size: 1200×800.
          </p>
        </div>
      </div>

      <div className="lg:col-span-2 space-y-4">
        {items.length === 0 && (
          <div className="card-surface p-10 text-center text-muted2">No gallery items yet. Add your first before/after above.</div>
        )}
        {items.map((g) => (
          <div key={g.id} className="card-surface overflow-hidden" data-testid={`gallery-row-${g.id}`}>
            <div className="grid grid-cols-2">
              <div className="relative h-44">
                <img src={g.before_url} alt="Before" className="w-full h-full object-cover" />
                <span className="absolute top-2 left-2 bg-ink/85 text-white text-[10px] uppercase tracking-[0.22em] px-2 py-0.5 rounded-full">Before</span>
              </div>
              <div className="relative h-44">
                <img src={g.after_url} alt="After" className="w-full h-full object-cover" />
                <span className="absolute top-2 left-2 bg-forest text-white text-[10px] uppercase tracking-[0.22em] px-2 py-0.5 rounded-full">After</span>
              </div>
            </div>
            <div className="p-5 flex items-center justify-between gap-4">
              <div className="text-sm">
                <div className="eyebrow">{g.city_slug}</div>
                {g.caption && <div className="text-ink/85 mt-1">{g.caption}</div>}
                <div className="text-xs text-muted2 mt-1">Order: {g.order}</div>
              </div>
              <button
                onClick={() => remove(g.id)}
                className="inline-flex items-center gap-2 text-xs text-red-700 hover:underline"
                data-testid={`gallery-delete-${g.id}`}
              >
                <Trash2 size={14} /> Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
